import { useMemo, useCallback, useReducer } from "react"
import { Outlet } from "react-router-dom"
import PropTypes from "prop-types"
import { chain, get, map, some, isString } from "lodash"
import { produce } from "immer"
import { Card, IconButton } from "@mui/material"
import { useQueryStringState } from "hooks/query-string"
import useAdjustedDates from "hooks/adjusted-dates"
import useIsConstituentsDownloadAllowed from "hooks/use-is-constituents-download-allowed"
import { useLastRouterMatchProp } from "hooks/router"
import { combineTanStackStatuses, ERROR, LOADING, OK } from "helpers/data-statuses"
import Loading from "components/loading"
import Error from "components/error"
import { useModelMetrics, useModelMetadata } from "resources/models"
import { usePositionSetsMetadata } from "./hooks/position-sets"
import useSelectedView from "./hooks/selected-view"
import { useContentSetsMetadata } from "resources/content-sets"
import { useResourceCurrency } from "resources/currencies"
import ShareMyView from "modules/share-my-view"
import { DATE } from "helpers/shapes"
import { getDefaultDateRangeProps } from "helpers/shareMyView"
import SelectedDate from "components/selected-date"
import { CloudDownload } from "@mui/icons-material"
import { downloadXlsx } from "helpers/csv"
import CompareNavigation from "./components/navigation"
import CompareItemHeader from "./components/item-header"
import CompareItemModifiers from "./components/item-modifiers"
import CompareAddButton from "./components/add-button"
import CompareDateSelector from "./components/date-selector"
import { parsePositionSet, computePositionSetKeys, NOT_FOUND_LABEL, PORTFOLIO, SECURITY, BENCHMARK, INDEX, ETF, BASKET } from "./position-sets"
import useUnsupportedSegmentToast from "./hooks/use-unsupported-segment-toast"
import "./style.scss"

const MAX_COLUMNS = 5
const EMPTY_ARRAY = []

function downloadReducer(state, action) {
  const { type, payload } = action
  const { key, data, sectionIndex } = payload || {}
  return produce(state, draft => {
    switch (type) {
      case "SET_DOWNLOAD_DATA":
        if (data === null) {
          delete draft.sheets[key]
        } else {
          draft.sheets[key] = {
            index: sectionIndex,
            data,
          }
        }
        break
      case "SET_DOWNLOAD_NAME_CHUNK":
        draft.fileNameChunks[payload.id] = payload.chunk
        break
    }
  })
}

export default function Compare({ modelId, mainPositionSet, contextBase, pagePortfolio, earliestDate: contextEarliestDate, latestDate: contextLatestDate, setPeriod }) {
  const [{ positionSets: queryStringPositionSets = EMPTY_ARRAY, selectedDate, selectedMetric }, updateQueryString] = useQueryStringState()
  const [modelMetadata, modelMetadataStatus] = useModelMetadata(modelId)
  // Currency is currently set for all position sets according to the currency of the main position set
  const [currency, currencyStatus] = useResourceCurrency(modelId, mainPositionSet)
  const [contentSets, contentSetsStatus] = useContentSetsMetadata()
  const [modelMetrics, modelMetricsStatus] = useModelMetrics(modelId)
  const isPerformance = useLastRouterMatchProp("isPerformance")
  const selectedView = useSelectedView()

  const [downloadData, dispatchDownload] = useReducer(downloadReducer, {
    sheets: {},
    fileNameChunks: {},
  })

  const resetSelectedDate = useCallback(() => {
    updateQueryString({ selectedDate: null })
  }, [updateQueryString])

  const positionSets = useMemo(() => {
    if (!mainPositionSet) {
      return queryStringPositionSets
    }

    return [
      mainPositionSet,
      ...queryStringPositionSets,
    ]
  }, [mainPositionSet, queryStringPositionSets])

  const [isDownloadAllowedStatus, isDownloadAllowed] = useIsConstituentsDownloadAllowed({ modelId, positionSets })

  const positionSetsWithBases = useMemo(() => {
    if (!contextBase) {
      return positionSets
    }

    return map(positionSets, ps => {
      if (ps.base !== undefined) {
        return ps
      }

      return {
        ...ps,
        base: { ...contextBase },
      }
    })
  }, [positionSets, contextBase])

  const [positionSetsMetadataStatus, { positionSetsMetadata, basesMetadata, earliestDate, latestDate }] = usePositionSetsMetadata(modelId, positionSetsWithBases, contextEarliestDate, contextLatestDate)
  const [adjustedDatesStatus, { period, adjustedStartDate, adjustedEndDate, datesAdjusted }] = useAdjustedDates(modelId, earliestDate, latestDate, mainPositionSet)

  const status = combineTanStackStatuses([adjustedDatesStatus, modelMetadataStatus, contentSetsStatus, positionSetsMetadataStatus, modelMetricsStatus, currencyStatus])

  const shareMyViewProps = useMemo(() => getDefaultDateRangeProps(period, adjustedStartDate, adjustedEndDate), [period, adjustedStartDate, adjustedEndDate])
  const isContextActive = !!contextBase

  const parsedPositionSets = useMemo(() => {
    if (status !== OK) {
      return null
    }

    return map(positionSets, (positionSet, index) => {
      const isFixed = !!mainPositionSet && index === 0
      return parsePositionSet(
        positionSet, // This is the original definition of the position set. Required for split long/short.
        positionSetsWithBases[index], // This is the original position set + whatever base it should use. Used to generate queries.
        positionSetsMetadata[index], // This is the metadata of the position set
        basesMetadata[index], // This is the metadata of the base
        adjustedStartDate, // This is the date that will be used to initialize position sets where type can't be used
        isFixed, // true if this is the main position set. Used to remove 'remove' option,
        isContextActive,
        isPerformance,
        modelMetadata,
        currency,
        pagePortfolio,
      )
    })
  }, [status, mainPositionSet, adjustedStartDate, positionSets, positionSetsWithBases, basesMetadata, positionSetsMetadata, isContextActive, isPerformance, modelMetadata, currency, pagePortfolio])

  const positionSetKeys = useMemo(() => computePositionSetKeys(parsedPositionSets ?? EMPTY_ARRAY), [parsedPositionSets])

  const hasModifiers = useMemo(() => some(parsedPositionSets, ps => ps?.additionalHeaders?.length > 0), [parsedPositionSets])
  const remainingColumns = MAX_COLUMNS - positionSets.length

  const affectedColumns = useMemo(() => parsedPositionSets
    ?.filter(ps => ps.hasBaseUnsupportedSegment)
    .map(ps => ({
      name: ps.unavailable ? NOT_FOUND_LABEL : ps.name,
      filters: ps.effectiveSegmentFilters,
      normalization: ps.normalization,
    })) ?? null, [parsedPositionSets])
  useUnsupportedSegmentToast(affectedColumns, pagePortfolio?.id, adjustedEndDate, currency)

  const setDownloadData = useCallback((data, key, sectionIndex) => dispatchDownload({
    type: "SET_DOWNLOAD_DATA",
    payload: { data, key, sectionIndex },
  }), [dispatchDownload])

  const setDownloadNameChunk = useCallback((id, chunk) =>
    dispatchDownload({
      type: "SET_DOWNLOAD_NAME_CHUNK",
      payload: { id, chunk },
    }), [dispatchDownload])

  async function handleDownload() {
    // fileName example: `{Portfolio Long Only} {Composition} ({Selected Metric}) Comparison {2016-12-27}.xlsx`,
    const { fileNameChunks } = downloadData

    const fileName = chain([
      get(positionSetsMetadata, "0.name"),
      fileNameChunks?.view,
      "Comparison",
      fileNameChunks?.metric ? `(${fileNameChunks.metric})` : "",
      selectedDate || adjustedEndDate,
    ])
      .compact()
      .join(" ")
      .value()

    const workbook = {
      sheets: chain(downloadData)
        .get("sheets")
        .sortBy(({ index }) => index)
        .map(({ data: generateSectionDataFn }) => generateSectionDataFn())
        .compact()
        .value(),
    }

    await downloadXlsx(
      fileName,
      workbook,
      {
        type: "compare2-download",
        location: "compare2",
      },
    )
  }

  function handleCompareDateChanged(selectedPeriod) {
    if (isString(selectedPeriod)) {
      updateQueryString({ startDate: null, endDate: null, period: selectedPeriod })
    } else {
      updateQueryString({ startDate: selectedPeriod.startDate, endDate: selectedPeriod.endDate, period: null })
    }
    setPeriod(selectedPeriod)
  }

  if (status === LOADING) {
    return <Loading />
  }

  if (status === ERROR) {
    return <Error />
  }

  return <div className="components_compare">
    <Card square elevation={1} className="compare-toolbar">
      <div>
        <CompareNavigation setDownloadNameChunk={setDownloadNameChunk} contentSets={contentSets} />
        <CompareDateSelector {...{
            period,
            earliestDate,
            latestDate,
            startDate: adjustedStartDate,
            endDate: adjustedEndDate,
            datesAdjusted,
            onChange: handleCompareDateChanged,
          }}
        />
        {selectedDate < adjustedEndDate && <SelectedDate date={selectedDate} resetHandler={resetSelectedDate} performance={isPerformance} />}
        <div className="right-aligned">
          <IconButton disabled={selectedView === "composition" && (isDownloadAllowedStatus !== OK || !isDownloadAllowed)} data-test-class="compare-download-button" onClick={handleDownload}>
            <CloudDownload />
          </IconButton>
          <ShareMyView size="medium" {...shareMyViewProps} />
        </div>
      </div>
      <hr />
      <div className="position-sets">
        <CompareAddButton modelId={modelId} disabled={remainingColumns <= 0} />
        {map(parsedPositionSets, (positionSet, i) => <CompareItemHeader key={positionSetKeys[i]} modelId={modelId} index={i} positionSet={positionSet} remainingColumns={remainingColumns} isContextActive={isContextActive} endDate={adjustedEndDate} pagePortfolio={pagePortfolio} />)}
      </div>
      {hasModifiers && <div className="position-sets-modifiers">
        <div />
        {map(parsedPositionSets, (positionSet, i) => <CompareItemModifiers key={i} positionSet={positionSet} currency={currency} endDate={adjustedEndDate} />)}
      </div>}
    </Card>

    <Outlet context={{
      modelId,
      modelMetadata,
      startDate: adjustedStartDate,
      endDate: adjustedEndDate,
      positionSets: parsedPositionSets,
      contextBase,
      currency,
      modelMetrics,
      selectedDate,
      selectedMetric,
      setDownloadData,
      setDownloadNameChunk,
    }}
    />
  </div>
}

Compare.propTypes = {
  modelId: PropTypes.string.isRequired,
  accountId: PropTypes.string.isRequired,
  mainPositionSet: PropTypes.shape({
    id: PropTypes.string.isRequired,
    type: PropTypes.oneOf([PORTFOLIO]).isRequired,
  }).isRequired,
  contextBase: PropTypes.shape({
    id: PropTypes.string.isRequired,
    type: PropTypes.oneOf([PORTFOLIO, SECURITY, BENCHMARK, INDEX, ETF, BASKET]).isRequired,
    experimentId: PropTypes.string,
  }),
  pagePortfolio: PropTypes.shape({
    id: PropTypes.string,
    alias: PropTypes.string,
  }),
  earliestDate: DATE.isRequired,
  latestDate: DATE.isRequired,
  setPeriod: PropTypes.func.isRequired,
}
